<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  LOCKED_THEME,
  SpBadge,
  SpButton,
  SpIcon,
  SpLogo,
  formatMoney,
  useTheme,
  type IconName,
} from '@servepod/ui';
import { useSession } from '@/stores/session';

const route = useRoute();
const router = useRouter();
const session = useSession();
const { resolved, toggle } = useTheme();

const mobileOpen = ref(false);
const menuOpen = ref(false);

/** Grouped so the menu reads as what you run (Resources) and who you are (Account). */
const NAV: { heading: string | null; items: { to: string; label: string; icon: IconName }[] }[] = [
  { heading: null, items: [{ to: '/dashboard', label: 'Overview', icon: 'dashboard' }] },
  {
    heading: 'Resources',
    items: [
      { to: '/pods', label: 'Pods', icon: 'pods' },
      { to: '/volumes', label: 'Volumes', icon: 'storage' },
    ],
  },
  {
    heading: 'Account',
    items: [
      { to: '/ssh-keys', label: 'SSH keys', icon: 'key' },
      { to: '/billing', label: 'Billing', icon: 'billing' },
      { to: '/settings', label: 'Settings', icon: 'settings' },
    ],
  },
];

const active = computed(() => (to: string) => route.path === to || route.path.startsWith(`${to}/`));

async function signOut() {
  await session.signOut();
  void router.push({ name: 'sign-in' });
}
</script>

<template>
  <div class="min-h-screen bg-canvas">
    <!-- A suspended account can still read and pay, so the banner explains rather than
         blocking, and points at the one action that resolves it. -->
    <div
      v-if="session.suspended"
      class="flex items-center justify-center gap-3 border-b border-danger-border bg-danger-subtle px-4 py-2.5 text-sm text-danger"
      role="alert"
    >
      <SpIcon name="warning" :size="16" />
      <span
        >This account is suspended. Pods cannot be created or started until it is reinstated.</span
      >
      <RouterLink to="/billing" class="font-medium underline underline-offset-2"
        >Review billing</RouterLink
      >
    </div>

    <div class="flex">
      <!-- Sidebar -->
      <aside
        class="fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-line bg-surface transition-transform duration-fast lg:translate-x-0 print:hidden"
        :class="mobileOpen ? 'translate-x-0' : '-translate-x-full'"
      >
        <div class="flex h-14 shrink-0 items-center gap-2.5 border-b border-line px-4">
          <RouterLink to="/dashboard" class="flex items-center" aria-label="ServePod home">
            <SpLogo :height="28" />
          </RouterLink>
        </div>

        <!-- The balance is shown permanently, directly under the logo, rather than buried
             in billing: running out is the single most common reason a launch fails, and
             a number always in view prevents that surprise. Hidden entirely for members
             without billing.read; the server sends billing: null for them. -->
        <div v-if="session.me?.billing" class="border-b border-line p-3">
          <RouterLink
            to="/billing"
            class="sp-balance-card block rounded-lg p-3"
            @click="mobileOpen = false"
          >
            <div class="flex items-center justify-between">
              <span class="sp-balance-muted text-2xs font-medium uppercase tracking-wider"
                >Balance</span
              >
              <SpIcon name="chevronRight" :size="13" class="sp-balance-muted" />
            </div>
            <p class="mt-1 text-lg font-semibold tracking-tight">
              {{ formatMoney(session.me?.billing?.balance) }}
            </p>
            <p class="sp-balance-muted text-2xs">
              {{ formatMoney(session.me?.billing?.spend_this_month) }} spent this month
            </p>
          </RouterLink>
        </div>

        <nav class="flex-1 overflow-y-auto p-3" aria-label="Main">
          <div v-for="group in NAV" :key="group.heading ?? 'top'" :class="group.heading && 'mt-5'">
            <p
              v-if="group.heading"
              class="mb-1.5 px-2.5 text-2xs font-medium uppercase tracking-wider text-ink-muted"
            >
              {{ group.heading }}
            </p>
            <div class="space-y-0.5">
              <RouterLink
                v-for="item in group.items"
                :key="item.to"
                :to="item.to"
                class="group flex items-center gap-2.5 rounded px-2.5 py-2 text-sm font-medium transition-colors duration-fast"
                :class="
                  active(item.to)
                    ? 'bg-accent-subtle text-ink'
                    : 'text-ink-secondary hover:bg-surface-hover hover:text-ink'
                "
                :aria-current="active(item.to) ? 'page' : undefined"
                @click="mobileOpen = false"
              >
                <SpIcon :name="item.icon" :size="17" />
                {{ item.label }}
              </RouterLink>
            </div>
          </div>
        </nav>

        <div class="border-t border-line p-3">
          <button
            type="button"
            class="flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-sm font-medium text-ink-secondary transition-colors duration-fast hover:bg-danger-subtle hover:text-danger"
            @click="signOut"
          >
            <SpIcon name="logout" :size="17" />
            Sign out
          </button>
        </div>
      </aside>

      <div
        v-if="mobileOpen"
        class="fixed inset-0 z-30 bg-black/50 lg:hidden"
        aria-hidden="true"
        @click="mobileOpen = false"
      />

      <!-- Main -->
      <!-- print:pl-0 goes with the hidden sidebar: the gutter it reserves would
           otherwise indent every printed page by 15rem of nothing. -->
      <div class="flex min-h-screen w-full flex-col lg:pl-60 print:pl-0">
        <header
          class="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-line bg-canvas/85 px-4 backdrop-blur lg:px-6 print:hidden"
        >
          <button
            type="button"
            class="grid h-9 w-9 place-items-center rounded text-ink-secondary transition-colors hover:bg-surface-hover lg:hidden"
            aria-label="Open navigation"
            @click="mobileOpen = true"
          >
            <SpIcon name="menu" :size="18" />
          </button>

          <div class="flex-1" />

          <SpButton size="sm" variant="primary" icon="plus" @click="router.push('/pods/new')">
            New pod
          </SpButton>

          <button
            v-if="LOCKED_THEME === null"
            type="button"
            class="grid h-9 w-9 place-items-center rounded text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink"
            :aria-label="resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
            @click="toggle"
          >
            <SpIcon :name="resolved === 'dark' ? 'sun' : 'moon'" :size="17" />
          </button>

          <div class="relative">
            <button
              type="button"
              class="flex h-9 items-center gap-2 rounded pl-1.5 pr-2 text-sm transition-colors hover:bg-surface-hover"
              aria-haspopup="menu"
              :aria-expanded="menuOpen"
              @click="menuOpen = !menuOpen"
            >
              <span
                class="grid h-7 w-7 place-items-center rounded-full bg-accent-subtle text-xs font-semibold uppercase text-accent"
              >
                {{ session.displayName.slice(0, 2) }}
              </span>
              <span class="hidden max-w-32 truncate font-medium sm:block">{{
                session.displayName
              }}</span>
              <SpIcon name="chevronDown" :size="13" class="text-ink-muted" />
            </button>

            <div
              v-if="menuOpen"
              class="absolute right-0 top-11 w-60 rounded-lg border border-line bg-surface-overlay p-1.5 shadow-lg"
              role="menu"
              @click="menuOpen = false"
            >
              <div class="border-b border-line px-2.5 pb-2.5 pt-1.5">
                <p class="truncate text-sm font-medium">{{ session.me?.user.email }}</p>
                <p class="mt-0.5 truncate text-xs text-ink-muted">
                  {{ session.me?.account.name }}
                </p>
                <SpBadge v-if="session.me?.is_staff" tone="accent" class="mt-2">Staff</SpBadge>
              </div>
              <RouterLink
                to="/settings"
                class="flex items-center gap-2.5 rounded px-2.5 py-2 text-sm text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink"
                role="menuitem"
              >
                <SpIcon name="settings" :size="15" /> Settings
              </RouterLink>
              <button
                type="button"
                class="flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-left text-sm text-ink-secondary transition-colors hover:bg-danger-subtle hover:text-danger"
                role="menuitem"
                @click="signOut"
              >
                <SpIcon name="logout" :size="15" /> Sign out
              </button>
            </div>
          </div>
        </header>

        <!-- Click-away for the account menu. Rendered under the menu, above the page. -->
        <div
          v-if="menuOpen"
          class="fixed inset-0 z-10"
          aria-hidden="true"
          @click="menuOpen = false"
        />

        <main class="flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <div class="mx-auto w-full max-w-7xl">
            <slot />
          </div>
        </main>
      </div>
    </div>
  </div>
</template>
